/**
 * 言語状態（日本語/英語）とその永続化（要件20）。
 *
 * - 既定は日本語（要件20-1）。
 * - 選択は localStorage に保存し、次回アクセス時に復元する。
 * - 閲覧者（未ログイン）も切り替えられる（UI 文言・地図ラベルの両方に反映）。
 */
import { useCallback, useState } from 'react';

export type Lang = 'ja' | 'en';

/** UI に並べる順序（MapToolbar の選択肢の順）。mapStyle.ts の MAP_STYLE_IDS と対称。 */
export const LANG_IDS: readonly Lang[] = ['ja', 'en'];

const STORAGE_KEY = 'reinvent_lang';

/** localStorage から言語を読む（無効値・未設定は既定 ja）。 */
function loadLang(): Lang {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'en' ? 'en' : 'ja';
  } catch {
    return 'ja';
  }
}

export interface UseLangResult {
  lang: Lang;
  setLang: (lang: Lang) => void;
  /** ja ↔ en を反転する。 */
  toggle: () => void;
}

/** 言語状態フック。App で1回呼び、各コンポーネントへ配布する。 */
export function useLang(): UseLangResult {
  const [lang, setLangState] = useState<Lang>(() => loadLang());

  const setLang = useCallback((next: Lang) => {
    setLangState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // 永続化失敗（プライベートモード等）は無視。セッション内の切替は効く。
    }
  }, []);

  const toggle = useCallback(() => {
    setLang(lang === 'ja' ? 'en' : 'ja');
  }, [lang, setLang]);

  return { lang, setLang, toggle };
}
